import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown, TrendingUp, Target, Zap, BarChart3, Shield, Mail } from "lucide-react";
import { API } from "@/lib/api";

const GRADES = [
  { value: "5th", label: "5th Grade" },
  { value: "6th", label: "6th Grade" },
  { value: "7th", label: "7th Grade" },
  { value: "8th", label: "8th Grade" },
  { value: "9th", label: "9th Grade" },
  { value: "10th", label: "10th Grade" },
  { value: "11th", label: "11th Grade" },
  { value: "12th", label: "12th Grade" },
  { value: "college", label: "College" },
];

const TIER_CONFIG = {
  "Elite": { color: "#F59E0B", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.4)", icon: Zap },
  "Above Average": { color: "#10B981", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.4)", icon: TrendingUp },
  "Average": { color: "#3B82F6", bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.4)", icon: Target },
  "Below Average": { color: "#EF4444", bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.4)", icon: BarChart3 },
};

const ScoreCard = ({ label, value, unit, rating, delay }) => {
  const cfg = TIER_CONFIG[rating.tier] || TIER_CONFIG["Average"];
  const Icon = cfg.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className="rounded-xl overflow-hidden"
      style={{ background: cfg.bg, border: `1.5px solid ${cfg.border}` }}
    >
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-400 uppercase tracking-wider">{label}</span>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: `${cfg.color}20`, border: `1px solid ${cfg.color}40` }}>
            <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
            <span className="text-xs font-bold" style={{ color: cfg.color }}>{rating.tier}</span>
          </div>
        </div>
        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-3xl font-black text-white">{value}</span>
          <span className="text-sm text-gray-500">{unit}</span>
        </div>
        {/* Benchmark bar */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-[10px] text-gray-500">
            <span>Avg: {rating.avg}{unit === "inches" ? '"' : ""}</span>
            <span>Above: {rating.above_avg}{unit === "inches" ? '"' : ""}</span>
            <span>Elite: {rating.elite}{unit === "inches" ? '"' : ""}</span>
          </div>
          <div className="relative h-2 bg-[#1E293B] rounded-full overflow-hidden">
            {/* Marker for avg */}
            <div className="absolute top-0 bottom-0 w-px bg-gray-600" style={{ left: `${(rating.avg / rating.elite) * 80}%` }} />
            {/* Marker for above avg */}
            <div className="absolute top-0 bottom-0 w-px bg-gray-600" style={{ left: `${(rating.above_avg / rating.elite) * 80}%` }} />
            {/* Athlete's score */}
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(100, (parseFloat(value) / rating.elite) * 80)}%` }}
              transition={{ delay: delay + 0.3, duration: 0.8, ease: "easeOut" }}
              className="absolute top-0 bottom-0 rounded-full"
              style={{ background: cfg.color }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const JumpReportPage = () => {
  const [form, setForm] = useState({
    first_name: "",
    email: "",
    grade: "",
    gender: "",
    sport: "",
    vertical_jump: "",
    broad_jump_feet: "",
    broad_jump_inches: "",
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const isValid = form.first_name && form.email && form.grade && form.gender
    && form.vertical_jump && form.broad_jump_feet !== "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/lead-magnet/jump-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.first_name,
          email: form.email,
          grade: form.grade,
          gender: form.gender,
          sport: form.sport,
          vertical_jump: parseFloat(form.vertical_jump),
          broad_jump_feet: parseInt(form.broad_jump_feet) || 0,
          broad_jump_inches: parseFloat(form.broad_jump_inches) || 0,
        }),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setReport(data.report);
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E17]">
      {/* Subtle grid bg */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, rgba(59,130,246,0.04) 1px, transparent 0)",
        backgroundSize: "40px 40px",
      }} />

      {/* Header */}
      <header className="relative z-10 border-b border-[#1E293B]/60">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Paladin" className="w-8 h-8" onError={(e) => { e.target.style.display = 'none'; }} />
            <div>
              <span className="text-white font-bold tracking-wider text-sm">PALADIN</span>
              <span className="text-[10px] text-gray-500 block tracking-widest">HEALTH & HUMAN PERFORMANCE</span>
            </div>
          </div>
          <a href="/login" className="text-xs text-gray-500 hover:text-white transition-colors">Coach Login</a>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-4 py-8 sm:py-12">
        <AnimatePresence mode="wait">
          {!report ? (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Hero */}
              <div className="text-center mb-10">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 mb-6">
                    <Zap className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Free Jump Assessment</span>
                  </div>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-4">
                    How Does Your Athlete
                    <br />
                    <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                      Stack Up?
                    </span>
                  </h1>
                  <p className="text-base sm:text-lg text-gray-400 max-w-xl mx-auto leading-relaxed">
                    Enter your athlete's jump scores and instantly see how they compare
                    to national benchmarks for their age and gender.
                  </p>
                </motion.div>
              </div>

              {/* Form */}
              <motion.form
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="max-w-lg mx-auto space-y-5"
              >
                <div className="rounded-2xl bg-[#111827] border border-[#1E293B] p-6 space-y-4">
                  <h3 className="text-sm font-bold text-gray-300 uppercase tracking-wider mb-1">Athlete Info</h3>

                  {/* Name + Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">First Name</label>
                      <input
                        type="text"
                        value={form.first_name}
                        onChange={(e) => update("first_name", e.target.value)}
                        placeholder="e.g. Jake"
                        className="w-full bg-[#0A0E17] border border-[#2D3B4F] rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none transition-colors placeholder:text-gray-600"
                        data-testid="lead-first-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Parent Email</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="parent@email.com"
                        className="w-full bg-[#0A0E17] border border-[#2D3B4F] rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none transition-colors placeholder:text-gray-600"
                        data-testid="lead-email"
                      />
                    </div>
                  </div>

                  {/* Grade + Gender */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="relative">
                      <label className="text-xs text-gray-500 mb-1 block">Grade</label>
                      <select
                        value={form.grade}
                        onChange={(e) => update("grade", e.target.value)}
                        className="w-full bg-[#0A0E17] border border-[#2D3B4F] rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none appearance-none transition-colors"
                        data-testid="lead-grade"
                      >
                        <option value="">Select Grade</option>
                        {GRADES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                      </select>
                      <ChevronDown className="absolute right-3 top-[30px] w-4 h-4 text-gray-500 pointer-events-none" />
                    </div>
                    <div className="relative">
                      <label className="text-xs text-gray-500 mb-1 block">Gender</label>
                      <select
                        value={form.gender}
                        onChange={(e) => update("gender", e.target.value)}
                        className="w-full bg-[#0A0E17] border border-[#2D3B4F] rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none appearance-none transition-colors"
                        data-testid="lead-gender"
                      >
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-[30px] w-4 h-4 text-gray-500 pointer-events-none" />
                    </div>
                  </div>

                  {/* Sport */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Sport (optional)</label>
                    <input
                      type="text"
                      value={form.sport}
                      onChange={(e) => update("sport", e.target.value)}
                      placeholder="e.g. Basketball, Soccer, Football"
                      className="w-full bg-[#0A0E17] border border-[#2D3B4F] rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none transition-colors placeholder:text-gray-600"
                      data-testid="lead-sport"
                    />
                  </div>
                </div>

                <div className="rounded-2xl bg-[#111827] border border-[#1E293B] p-6 space-y-4">
                  <h3 className="text-sm font-bold text-gray-300 uppercase tracking-wider mb-1">Jump Scores</h3>

                  {/* Vertical Jump */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Vertical Jump (inches)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={form.vertical_jump}
                      onChange={(e) => update("vertical_jump", e.target.value)}
                      placeholder="e.g. 22"
                      className="w-full bg-[#0A0E17] border border-[#2D3B4F] rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none transition-colors placeholder:text-gray-600"
                      data-testid="lead-vertical-jump"
                    />
                  </div>

                  {/* Broad Jump — feet + inches */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Broad / Standing Long Jump</label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <input
                          type="number"
                          value={form.broad_jump_feet}
                          onChange={(e) => update("broad_jump_feet", e.target.value)}
                          placeholder="Feet"
                          className="w-full bg-[#0A0E17] border border-[#2D3B4F] rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none transition-colors placeholder:text-gray-600"
                          data-testid="lead-broad-jump-feet"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">ft</span>
                      </div>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.5"
                          value={form.broad_jump_inches}
                          onChange={(e) => update("broad_jump_inches", e.target.value)}
                          placeholder="Inches"
                          className="w-full bg-[#0A0E17] border border-[#2D3B4F] rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none transition-colors placeholder:text-gray-600"
                          data-testid="lead-broad-jump-inches"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">in</span>
                      </div>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="text-sm text-red-400 text-center bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!isValid || loading}
                  className="w-full py-3.5 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: isValid ? "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)" : "#1E293B",
                  }}
                  data-testid="lead-submit-btn"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white" />
                  ) : (
                    <>
                      Get My Free Report
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center gap-2 text-[10px] text-gray-600">
                  <Shield className="w-3 h-3" />
                  <span>Your info is secure and will never be shared.</span>
                </div>
              </motion.form>

              {/* Social proof */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-center mt-12 space-y-3"
              >
                <p className="text-xs text-gray-600 uppercase tracking-widest">Trusted by coaches and parents across the country</p>
                <div className="flex items-center justify-center gap-8 text-gray-700">
                  <span className="text-sm font-medium">500+ Athletes Assessed</span>
                  <span className="w-1 h-1 rounded-full bg-gray-700" />
                  <span className="text-sm font-medium">50+ Schools & Clubs</span>
                </div>
              </motion.div>
            </motion.div>
          ) : (
            /* ============ REPORT RESULTS ============ */
            <motion.div
              key="report"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-lg mx-auto"
            >
              {/* Report Header */}
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                  className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                  style={{
                    background: (TIER_CONFIG[report.composite.tier] || TIER_CONFIG["Average"]).bg,
                    border: `2px solid ${(TIER_CONFIG[report.composite.tier] || TIER_CONFIG["Average"]).border}`,
                  }}
                >
                  {React.createElement((TIER_CONFIG[report.composite.tier] || TIER_CONFIG["Average"]).icon, {
                    className: "w-7 h-7",
                    style: { color: (TIER_CONFIG[report.composite.tier] || TIER_CONFIG["Average"]).color }
                  })}
                </motion.div>
                <h2 className="text-2xl sm:text-3xl font-black text-white mb-1">
                  {report.first_name}'s Jump Report
                </h2>
                <p className="text-sm text-gray-500">
                  {report.gender === "male" ? "Male" : "Female"} &bull; {report.grade_label} &bull; {report.sport || "General"}
                </p>
              </div>

              {/* Composite Score */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="rounded-2xl p-6 mb-6 text-center"
                style={{
                  background: "linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(16,185,129,0.06) 100%)",
                  border: "1.5px solid rgba(59,130,246,0.25)",
                }}
              >
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">Overall Rating</p>
                <p className="text-3xl font-black mb-1" style={{
                  color: (TIER_CONFIG[report.composite.tier] || TIER_CONFIG["Average"]).color
                }}>
                  {report.composite.tier}
                </p>
                <p className="text-sm text-gray-400">
                  Estimated {report.composite.percentile}th percentile for {report.grade_label} {report.gender === "male" ? "males" : "females"}
                </p>
              </motion.div>

              {/* Score Cards */}
              <div className="space-y-4 mb-8">
                <ScoreCard
                  label="Vertical Jump"
                  value={report.vertical_jump}
                  unit="inches"
                  rating={report.vj_rating}
                  delay={0.3}
                />
                <ScoreCard
                  label="Broad Jump"
                  value={report.broad_jump_display}
                  unit=""
                  rating={{
                    ...report.bj_rating,
                    avg: `${Math.floor(report.bj_rating.avg / 12)}'${report.bj_rating.avg % 12}"`,
                    above_avg: `${Math.floor(report.bj_rating.above_avg / 12)}'${report.bj_rating.above_avg % 12}"`,
                    elite: `${Math.floor(report.bj_rating.elite / 12)}'${report.bj_rating.elite % 12}"`,
                  }}
                  delay={0.5}
                />
              </div>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="rounded-2xl p-6 text-center space-y-4"
                style={{
                  background: "linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(245,158,11,0.03) 100%)",
                  border: "1.5px solid rgba(245,158,11,0.3)",
                }}
              >
                <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto">
                  <Mail className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="text-lg font-bold text-white">Want the Full Picture?</h3>
                <p className="text-sm text-gray-400 leading-relaxed max-w-sm mx-auto">
                  This is a simplified snapshot. A full Paladin assessment includes squat jump,
                  RSI, approach vertical, force plate data, and a personalized training plan.
                </p>
                <a
                  href="https://www.paladinhhp.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm text-black transition-colors"
                  style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)" }}
                  data-testid="lead-cta-full-assessment"
                >
                  Book a Full Assessment
                  <ArrowRight className="w-4 h-4" />
                </a>
                <p className="text-[10px] text-gray-600">
                  Check your inbox — we'll send training tips and assessment updates.
                </p>
              </motion.div>

              {/* Try Again */}
              <div className="text-center mt-6">
                <button
                  type="button"
                  onClick={() => { setReport(null); setForm({ first_name: "", email: "", grade: "", gender: "", sport: "", vertical_jump: "", broad_jump_feet: "", broad_jump_inches: "" }); }}
                  className="text-sm text-gray-500 hover:text-white transition-colors"
                  data-testid="lead-try-again"
                >
                  Test Another Athlete
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[#1E293B]/40 mt-16">
        <div className="max-w-5xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
          <span>Paladin Health & Human Performance</span>
          <div className="flex items-center gap-4">
            <a href="/legal/privacy" className="hover:text-white transition-colors">Privacy</a>
            <a href="/legal/terms" className="hover:text-white transition-colors">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default JumpReportPage;
